import locationsList from '../static-content/locale-menu.json';

import USMenuSections from '../static-content/menu/en-us-menu.json';
import AUMenuSections from '../static-content/menu/en-au-menu.json';
import GBMenuSections from '../static-content/menu/en-gb-menu.json';
import DEMenuSections from '../static-content/menu/de-de-menu.json';
import INMenuSections from '../static-content/menu/en-in-menu.json';

import { ChevronDown, Search } from 'react-feather';
import HMFlag from '../assets/icons/flags/round/australia.svg';
import DEFlag from '../assets/icons/flags/round/germany.svg';
import GBFlag from '../assets/icons/flags/round/uk.svg';
import USFlag from '../assets/icons/flags/round/us.svg';
import INFlag from '../assets/icons/flags/round/india.svg';
import NZFlag from '../assets/icons/flags/round/new-zealand.svg';
import localeMobileHeroImage from '../images/menu/locale-mobile.png';
import localeHeroImage from '../images/menu/locale.png';
import SvgBackArrow from '../svg-components/svg-back-arrow';
import SvgClose from '../svg-components/svg-close';
import SvgCrederaLogo from '../svg-components/svg-credera-logo';
import SvgCrederaLogoName from '../svg-components/svg-credera-logo-name';
import SvgGlobalLogo from '../svg-components/svg-global-logo';
import SvgMenu from '../assets/icons/menu/burger-menu.svg';
import SvgTopNavbarSearchIcon from '../svg-components/svg-top-navbar-search-icon';

import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import NavigationSection from '../models/NavigationSection/NavigationSection';

import {
  animation,
  Button,
  Container,
  Heading,
  InternalLink,
  ExternalLink,
  Text,
  colors,
  fontSizes,
  media
} from '../ui-components';

import { checkUrlLocale } from '../utils/check-url-locale';
import updateLocationUrl from '../utils/update-user-locale';
import { isInternalLink } from '../utils/link-handler';
import { useSearchActions } from '@yext/search-headless-react';
import CustomSearchBar from './search/searchbar';

/**
 * Nav Header Breakpoints
 * Size | From Design | Existing
 * SM   |    783      | mediumBottom 768px
 * MD   |    900      | mediumTop 960px
 * LG   |    1112     | largeTop 1280px
 */

/**
 * Component that renders the application header
 *
 * @component AppHeader
 * 
 * @returns {JSX}
 */
const AppHeader = () => {
  const [currentMenu, setCurrentMenu] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [bodyScrollLocked, setBodyScrollLocked] = useState(false);

  const MENU_MOBILE = 'mobile';
  const MENU_SEARCH = 'search';
  const MENU_LOCALE = 'locale';

  const data = useStaticQuery(graphql`
    query NavigationSectionQuery {
      navigationSections: allContentfulNavigationSection {
        edges {
          node {
            ...CompleteNavigationSection
          }
        }
      }
      aboutusImage: file(relativePath: { eq: "menu/about-us.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 600)
        }
      }
      careersImage: file(relativePath: { eq: "menu/careers.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 600)
        }
      }
      industriesImage: file(relativePath: { eq: "menu/industries.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 600)
        }
      }
      insightsImage: file(relativePath: { eq: "menu/insights.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 600)
        }
      }
      partnershipsImage: file(relativePath: { eq: "menu/partnerships.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 600)
        }
      }
      servicesImage: file(relativePath: { eq: "menu/services.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 600)
        }
      }
    }
  `);
  const dynamicNavigationSections = data.navigationSections.edges.map(edge => {
    return new NavigationSection(edge.node);
  });

  let MenuSections = USMenuSections;

  if (typeof window !== 'undefined') {
    let locale = checkUrlLocale();

    if (locale === '/en-au') {
      MenuSections = AUMenuSections;
    } else if (locale === '/en-gb') {
      MenuSections = GBMenuSections;
    } else if (locale === '/de-de') {
      MenuSections = DEMenuSections;
    } else if (locale === '/en-in') {
      MenuSections = INMenuSections;
    } else {
      MenuSections = USMenuSections;
    }
  }

  //locks the page from being able to be scrolled while the menu is open in mobile
  const mobileBodyLock = () => {
    setBodyScrollLocked(true);
    document.getElementsByTagName('html')[0].style.overflow = 'hidden';
  };

  //locks the page from being able to be scrolled while the menu is open in mobile
  const mobileBodyUnlock = () => {
    document.getElementsByTagName('html')[0].style.overflow = '';
    setBodyScrollLocked(false);
  };

  /**
   * Locks or unlocks the body of the document when the size is mobile
   */
  const mobileLockToggle = () => {
    if (!bodyScrollLocked) {
      mobileBodyLock();
    } else {
      mobileBodyUnlock();
    }
  };

  /**
   * Toggles the active menu. If the currentMenu title is passed in, the menu is closed (e.g., a
   * user clicks on the 'Services' button and clicks on it again).
   *
   * @function toggleMenu
   *
   * @param {string} title the title of the menu to toggle
   */
  const toggleMenu = title => {
    //if we are in a submenu, keep the body locked
    if (currentMenu !== '' && currentMenu !== MENU_MOBILE && title === MENU_MOBILE) {
      mobileBodyLock();
    }
    setCurrentMenu(currentMenu === title ? '' : title);
  };

  /**
   * Toggles the active menu when a nav button is hovered over. Prevents menus from being toggled
   * on hover if the search bar or locale menu are open.
   *
   * @function hoverToggleMenu
   *
   * @param {string} title the title of the menu to toggle
   */
  const hoverToggleMenu = title => {
    if (currentMenu !== MENU_SEARCH && currentMenu !== MENU_LOCALE) {
      setCurrentMenu(title);
    }
  };

  /**
   * Closes the menu and sets the appropriate state values to false/null.
   *
   * @function closeMenu
   */
  const closeMenu = () => {
    setCurrentMenu('');
    mobileBodyUnlock();
  };

  /**
   * Unlocks the body for scrolling if we navigate to a different url (and therefore close the menu)
   */
  const handleSubmenuUnlock = e => {
    if (
      e?.target?.href !== window?.location?.origin + window?.location?.pathname &&
      e?.target?.href !== window?.location?.href
    ) {
      mobileBodyUnlock();
    }
  };

  /**
   * Handles when a user clicks outside of the header/nav menu. This is only triggered when the
   * search bar or locale menu is open. The user must click the overlay to close the menu.
   *
   */
  useEffect(() => {
    const handleOutsideClick = event => {
      if (event) {
        if (typeof event.target.className !== 'object' && event?.target?.className?.includes('close-menu')) {
          closeMenu();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  /**
   * Adds event listeners to when the window is resized. Sets state variables that are used when
   * rendering the nav menu.
   */
  useEffect(() => {
    setIsCollapsed(window.innerWidth <= parseInt(media.largeTop));
    setIsMobile(window.innerWidth <= parseInt(media.mediumBottom));

    function handleResize() {
      // If mobile menu is open when the screen expands, close it and unlock the screen
      if (bodyScrollLocked && window.innerWidth > parseInt(media.largeTop)) {
        closeMenu();
        mobileBodyUnlock();
      } else if (window.innerWidth <= parseInt(media.largeTop) && !bodyScrollLocked) {
        //ensures that the menu closes whenever the Header collapses
        closeMenu();
      }

      setIsCollapsed(window.innerWidth <= parseInt(media.largeTop));
      setIsMobile(window.innerWidth <= parseInt(media.mediumBottom));
    }
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [currentMenu === MENU_MOBILE]);

  /**
   * Checks if the menu section is enabled in Contentful.
   *
   * @function isSectionEnabled
   *
   * @param {*} title the title of the menu section
   *
   * @returns {boolean} if the section is enabled
   */
  const isSectionEnabled = title => {
    const dynamicMenuData = dynamicNavigationSections?.find(section => {
      return section.title === title;
    });

    return !(dynamicMenuData && !dynamicMenuData?.enabled);
  };

  /**
   * The TopNavbar component sits above the Navbar and is only visible when the Navbar
   * is not condensed.
   *
   * @function TopNavbar
   */
  const TopNavbar = () => {
    return (
      <TopNavbarWrapper>
        <TopNavbarContainer>
          <MenuLink key={0} to={'/contact'}>
            Contact
          </MenuLink>
          <SearchButton />
          {currentMenu === MENU_SEARCH && <SearchMenu />}
          <LocaleSelectorButton />
          {currentMenu === MENU_LOCALE && <LocaleMenu />}
        </TopNavbarContainer>
      </TopNavbarWrapper>
    );
  };

  const SectionMenuButton = props => {
    const { section, index } = props;
    return (
      <NavbarSection>
        <MenuWrapper
          className="menu-section"
          sectionEnabled={currentMenu !== MENU_LOCALE && currentMenu !== MENU_SEARCH}
          onMouseLeave={() => {
            if (currentMenu !== MENU_LOCALE && currentMenu !== MENU_SEARCH) closeMenu();
          }}
        >
          <MenuButton
            key={index + 1}
            onClick={() => toggleMenu(section.title)}
            onMouseEnter={() => hoverToggleMenu(section.title)}
          >
            {section.title}
            <ChevronDown size={20} />
          </MenuButton>

          <MenuContainer menu={section.title} />
        </MenuWrapper>
        <MenuOpenOverlay className="close-menu" onMouseEnter={() => closeMenu()} />
      </NavbarSection>
    );
  };

  /**
   * The Navbar component is what a user interacts with to open the nav menu
   *
   * @function Navbar
   */
  const Navbar = () => {
    const renderFullNavbar = () => {
      return (
        <>
          <div style={{ width: '198px', height: '100%' }}>
            <InternalLink id="crederaLogo" to="/" aria-label="Back to Homepage">
              <SvgCrederaLogoName height={'80px'} />
            </InternalLink>
          </div>
          <NavbarMenu>
            {MenuSections.map(
              (section, index) =>
                isSectionEnabled(section.title) && <SectionMenuButton section={section} index={index} key={section} />
            )}
          </NavbarMenu>
        </>
      );
    };

    const renderCondensedNavbar = () => {
      return (
        <>
          <MenuTrigger
            onClick={() => {
              mobileLockToggle();
              toggleMenu(MENU_MOBILE);
            }}
            src={SvgMenu}
            alt={'Menu Icon'}
          />
          <div
            onClick={() => {
              closeMenu();
            }}
          >
            <InternalLink id="crederaLogo" to="/" aria-label="Back to Homepage">
              <SvgCrederaLogo />
            </InternalLink>
          </div>
          <SearchButton />

          {MenuSections.map(
            section => isSectionEnabled(section.title) && <MenuContainer menu={section.title} key={section} />
          )}
          {currentMenu === MENU_SEARCH && <SearchMenu />}
          {currentMenu === MENU_LOCALE && <LocaleMenu />}
        </>
      );
    };

    return (
      <NavbarWrapper>
        <NavbarContainer className="header">
          {!isCollapsed && renderFullNavbar()}
          {isCollapsed && renderCondensedNavbar()}
        </NavbarContainer>
      </NavbarWrapper>
    );
  };

  /**
   * The MenuContainer component is the outer container of all the different menu components.
   *
   * @function MenuContainer
   *
   * @param {object} props
   */
  const MenuContainer = props => {
    const { menu } = props;

    const renderMenu = menu => {
      if (menu) {
        return <Submenu menu={menu} />;
      }
    };

    /**
     * Renders the navbar that exists only when the menu is collapsed (not to be confused with the
     * Navbar component that lives at the top of the page)
     *
     * @function renderCollapseMenuNavbar
     */
    const renderCollapsedMenuNavbar = () => {
      return (
        <>
          <NavbarContainer>
            <IconDiv onClick={() => toggleMenu(MENU_MOBILE)}>{currentMenu != MENU_MOBILE && <SvgBackArrow />}</IconDiv>
            <IconDiv
              onClick={() => {
                mobileLockToggle();
                closeMenu();
              }}
            >
              <SvgClose />
            </IconDiv>
          </NavbarContainer>
        </>
      );
    };

    return (
      <SubmenuWrapper className={'submenu'} mobileShow={currentMenu === menu}>
        {currentMenu === menu && (
          <SubmenuContainer>
            {isCollapsed && renderCollapsedMenuNavbar()}
            {renderMenu(menu)}
          </SubmenuContainer>
        )}
      </SubmenuWrapper>
    );
  };

  /**
   * The Submenu component is a submenu component rendered by the MenuContainer and is what you
   * would consider the "main menu" that is shown after a user clicks on a button in the Navbar,
   * this is where the bulk of the menu content lives.
   *
   * @function Submenu
   *
   * @param {object} props
   */
  const Submenu = props => {
    const { menu } = props;

    const staticMenuData = MenuSections.find(section => {
      return section.title === menu;
    });
    const dynamicMenuData = dynamicNavigationSections?.find(section => {
      return section.title === staticMenuData.title;
    });

    const hasOfferings = staticMenuData.links[0].offerings ? true : false;
    const hasFeaturedContent =
      dynamicMenuData?.featuredContent || staticMenuData?.featuredContentTagline ? true : false;

    const isPartnerships = staticMenuData.title === 'Partnerships';

    /**
     * Renders the nav section hero image
     *
     * @function renderSectionHeroImage
     */
    const renderSectionHeroImage = () => {
      const alt = dynamicMenuData?.heroImage?.title || staticMenuData?.title?.toLowerCase().replace(' ', '');
      const image = dynamicMenuData?.heroImage?.fluid || getImage(data[`${alt}Image`]);

      if (image && alt === 'careers' || alt === 'insights' || isCollapsed) {
        return <SectionRightHero image={image} alt={`${alt} menu image`} />;
      } else {
        return <SectionLeftHero image={image} alt={`${alt} menu image`} />;
      }
    };

    /**
     * Renders the offerings list of the nav section
     *
     * @function renderOfferingsSection
     */
    const renderOfferingsSection = () => {
      const linksWithOfferings = staticMenuData.links.filter(link => link.offerings && link.offerings.length > 0);
      const linksWithoutOfferings = staticMenuData.links.filter(link => !link.offerings || link.offerings.length === 0);

      return (
        <>
          <SectionOfferingsList hasLinksWithoutOfferings={linksWithoutOfferings.length > 0}>
            {linksWithOfferings.map(
              (link, index) =>
                link.offerings && (
                  <SectionOfferingsLI key={index}>
                    <SectionLink
                      onClick={e => handleSubmenuUnlock(e)}
                      to={`${link.url}`}
                      aria-label={`Go to the ${link.title} page`}
                    >
                      {link.title}
                    </SectionLink>
                    <ul>
                      {link.offerings &&
                        link.offerings.map((offering, index) => (
                          <li key={index}>
                            <OfferingLink
                              onClick={e => handleSubmenuUnlock(e)}
                              to={`${offering.url}`}
                              aria-label={`Go to the ${offering.title} page`}
                            >
                              {offering.title}
                            </OfferingLink>
                          </li>
                        ))}
                    </ul>
                  </SectionOfferingsLI>
                )
            )}
          </SectionOfferingsList>
          {linksWithoutOfferings && linksWithoutOfferings.length > 0 && (
            <SectionLinksContainer>
              {linksWithoutOfferings.map((link, index) => (
                <SectionLink
                  onClick={e => handleSubmenuUnlock(e)}
                  key={index}
                  to={`${link.url}`}
                  aria-label={`Go to the ${link.title} page`}
                >
                  {link.title}
                </SectionLink>
              ))}
            </SectionLinksContainer>
          )}
        </>
      );
    };

    /**
     * Renders only the section links of a nav section when it does not contain offerings
     *
     * @function renderSectionLinks
     */
    const renderSectionLinks = () => {
      if (isPartnerships) {
        const links = staticMenuData.links.sort((a, b) => a.title.localeCompare(b.title));
        const chunkSize = 6;
        const columns = [];

        for (let i = 0; i < links.length; i += chunkSize) {
          columns.push(links.slice(i, i + chunkSize));
        }

        return (
          <SectionLinksColumns>
            {columns.map((column, columnIndex) => (
              <SectionLinksContainer key={columnIndex}>
                {column.map((link, index) => (
                  <SectionLink
                    onClick={e => handleSubmenuUnlock(e)}
                    key={index}
                    to={`${link.url}`}
                    aria-label={`Go to the ${link.title} page`}
                  >
                    {link.title}
                  </SectionLink>
                ))}
              </SectionLinksContainer>
            ))}
          </SectionLinksColumns>
        );
      }


      return (
        <SectionLinksContainer>
          {staticMenuData.links.map((link, index) => (
            <SectionLink
              onClick={e => handleSubmenuUnlock(e)}
              key={index}
              to={`${link.url}`}
              aria-label={`Go to the ${link.title} page`}
            >
              {link.title}
            </SectionLink>
          ))}
        </SectionLinksContainer>
      );
    };

    /**
     * Parses the featured content url based on the content type from Contentful
     *
     * @function getDynamicFeaturedContetnUrl
     */
    const getDynamicFeaturedContentUrl = () => {
      const content = dynamicMenuData?.featuredContent;

      if (content) {
        switch (content.__typename) {
          case 'ContentfulAssessment':
            return `/${content.slug}`;
          case 'ContentfulCaseStudy':
            return `/case-studies/${content.slug}`;
          case 'ContentfulInsight':
            return `/insights/${content.slug}`;
          case 'ContentfulOffice':
            return `/about-us/our-offices/${content.slug}`;
          case 'ContentfulPartnership':
            return `/partnerships/${content.slug}`;
          case 'ContentfulService':
            return `/services/${content.slug}`;
          case 'ContentfulServiceLine':
            return `/services/${content.service[0].slug}/${content.slug}`;
          default:
            return null;
        }
      }
      return null;
    };

    /**
     * Renders the featured content banner beneath the submenu
     *
     * @function renderFeaturedContentBanner
     */
    const renderFeaturedContentBanner = () => {
      return (
        <FeaturedContentBanner>
          <FeaturedContentContainer>
            <FeaturedContentBannerTagline>
              {dynamicMenuData?.featuredContentTagline
                ? dynamicMenuData?.featuredContentTagline
                : staticMenuData.featuredContentTagline}
            </FeaturedContentBannerTagline>
            <FeaturedContentBannerLink
              to={
                dynamicMenuData?.featuredContent
                  ? getDynamicFeaturedContentUrl(dynamicMenuData?.featuredContent)
                  : staticMenuData.featuredContentUrl
              }
              withArrow
            >
              {dynamicMenuData?.ctaText ? dynamicMenuData?.ctaText : 'Explore Further'}
            </FeaturedContentBannerLink>
          </FeaturedContentContainer>
        </FeaturedContentBanner>
      );
    };

    /**
     * Renders the featured content tagline (only rendered in mobile view)
     *
     * @function renderFeaturedContentTagline
     */
    const renderFeaturedContentTagline = () => {
      return (
        <MobileFeaturedContent>
          <MobileFeaturedContentTagline>
            {dynamicMenuData?.featuredContentTagline
              ? dynamicMenuData?.featuredContentTagline
              : staticMenuData.featuredContentTagline}
          </MobileFeaturedContentTagline>
          <OverviewLink
            to={
              dynamicMenuData?.featuredContent
                ? getDynamicFeaturedContentUrl(dynamicMenuData?.featuredContent)
                : staticMenuData.featuredContentUrl
            }
            withArrow
          >
            {dynamicMenuData?.ctaText ? dynamicMenuData?.ctaText : 'Explore Further'}
          </OverviewLink>
          <MobileSpacer />
        </MobileFeaturedContent>
      );
    };

    return (
      <SubmenuContentWrapper>
        <SubmenuContent hasOfferings={hasOfferings} mobileVisible={currentMenu === menu} justifyLeft={isPartnerships}>
          <SectionOverview withHeroImage={hasOfferings}>
            <Overview>
              <SectionTitle level={1}>{staticMenuData.title}</SectionTitle>
              <SectionDescription>{staticMenuData.description}</SectionDescription>
              {isInternalLink(staticMenuData.overviewUrl) ? (
                <OverviewLink onClick={e => handleSubmenuUnlock(e)} to={`${staticMenuData.overviewUrl}`} withArrow>
                  {staticMenuData.overviewCTA || 'Read Overview'}
                </OverviewLink>
              ) : (
                <OverviewExternalLink href={`${staticMenuData.overviewUrl}`} withArrow>
                  {staticMenuData.overviewCTA || 'Read Overview'}
                </OverviewExternalLink>
              )}
            </Overview>
            {(hasOfferings || isCollapsed || isPartnerships) && renderSectionHeroImage()}
          </SectionOverview>
          {isCollapsed && <MobileSpacer/>}
          {hasFeaturedContent && isMobile && renderFeaturedContentTagline()}
          {hasOfferings ? renderOfferingsSection() : renderSectionLinks()}
          {!hasOfferings && !isCollapsed && !isPartnerships && renderSectionHeroImage()}
        </SubmenuContent>
        {hasFeaturedContent && !isMobile && renderFeaturedContentBanner()}
      </SubmenuContentWrapper>
    );
  };

  /**
   * The MobileMenu component is a submenu component rendered by the MenuContainer that is only
   * displayed when in "collapsed" menu mode. It can be thought of as a collapsed version of the
   * Navbar component, as it has the same content and functionality.
   *
   * @function MobileMenu
   */
  const MobileMenu = () => {
    return (
      <MobileMenuWrapper>
        <NavbarContainer>
          <IconDiv onClick={() => toggleMenu(MENU_MOBILE)}>{currentMenu != MENU_MOBILE && <SvgBackArrow />}</IconDiv>
          <IconDiv
            onClick={() => {
              mobileLockToggle();
              closeMenu();
            }}
          >
            <SvgClose />
          </IconDiv>
        </NavbarContainer>
        <MobileMenuContent>
          <PrimaryMobileLinks>
            {MenuSections.map(
              (section, index) =>
                isSectionEnabled(section.title) &&
                (section.title === 'Contact Us' ? (
                  <MobileMenuLink key={index} to={'/contact'}>
                    Contact Us
                  </MobileMenuLink>
                ) : (
                  <>
                    <MobileMenuButton key={index} onClick={() => toggleMenu(section.title)}>
                      {section.title}
                    </MobileMenuButton>
                  </>
                ))
            )}
          </PrimaryMobileLinks>
          <MobileSpacer />
          <SecondaryMobileLinks>
            <MenuLink to={'/contact'} onClick={() => mobileLockToggle()}>
              Contact
            </MenuLink>
            <MobileLocaleButton onClick={() => toggleMenu(MENU_LOCALE)}>
              <SvgGlobalLogo />
              {(typeof window !== 'undefined' && sessionStorage.getItem('userLocation')) || 'Global'}
            </MobileLocaleButton>
          </SecondaryMobileLinks>
        </MobileMenuContent>
      </MobileMenuWrapper>
    );
  };

  const LocaleSelectorButton = () => {
    return (
      <LocalePillButton
        buttonstyle="pill"
        onClick={() => toggleMenu(MENU_LOCALE)}
        selected={currentMenu === MENU_LOCALE}
      >
        <SvgGlobalLogo />
        {(typeof window !== 'undefined' && sessionStorage.getItem('userLocation')) || 'Global'}
      </LocalePillButton>
    );
  };

  /**
   * The LocaleMenu component is a submenu component rendered by the MenuContainer that is displayed
   * when a user clicks on the locale selector button.
   *
   * @function LocaleMenu
   */
  const LocaleMenu = () => {
    const [show, setShow] = useState(false);

    useEffect(() => {
      if (currentMenu === MENU_LOCALE) {
        setShow(true);
      } else {
        setShow(false);
      }

      //If there is a menu that isnt search and the screen collapses, lock the screen
      if (
        currentMenu !== '' &&
        currentMenu !== MENU_SEARCH &&
        typeof window !== 'undefined' &&
        window.innerWidth <= parseInt(media.largeTop)
      ) {
        mobileBodyLock();
      } else {
        mobileBodyUnlock();
      }
    }, [currentMenu]);

    const getFlagImage = flagName => {
      switch (flagName) {
        case 'USFlag':
          return <CountryFlag src={USFlag} alt="USA Country Flag" />;
        case 'DEFlag':
          return <CountryFlag src={DEFlag} alt="Germany Country Flag" />;
        case 'GBFlag':
          return <CountryFlag src={GBFlag} alt="Great Britain Country Flag" />;
        case 'HMFlag':
          return <CountryFlag src={HMFlag} alt="Australia Country Flag" />;
        case 'INFlag':
          return <CountryFlag src={INFlag} alt="India Country Flag" />;
        case 'NZFlag':
          return <CountryFlag src={NZFlag} alt="New Zealand Country Flag" />;
        default:
          return null;
      }
    };

    if (typeof window !== 'undefined') {
      return (
        <>
          <LocaleSubmenuContent className="locale-menu" show={show} mobileVisible={show}>
            {isCollapsed && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 20 }}>
                <IconDiv onClick={() => toggleMenu(MENU_MOBILE)}>
                  {currentMenu != MENU_MOBILE && <SvgBackArrow />}
                </IconDiv>
                <IconDiv onClick={() => closeMenu()}>
                  <SvgClose />
                </IconDiv>
              </div>
            )}
            <LocaleWrapper>
              <LocaleContent>
                <div>
                  <LocaleTitle level={4}>{locationsList.title}</LocaleTitle>
                  <LocaleDescription>{locationsList.description}</LocaleDescription>
                </div>
                <MobileDivider />
                <LocaleList>
                  {locationsList.locations.map(
                    (locale, index) =>
                      locale.location != 'Global' && (
                        <LocaleLocationButton
                          key={index}
                          onClick={() => updateLocationUrl(locale)}
                          aria-label={`Set your location to ${locale.location}`}
                        >
                          {getFlagImage(locale.countryFlag)}
                          <LocaleText level={5}>{locale.location}</LocaleText>
                        </LocaleLocationButton>
                      )
                  )}
                </LocaleList>
              </LocaleContent>

              <LocaleMenuHeroImage
                src={isMobile ? localeMobileHeroImage : localeHeroImage}
                alt="Globe with magnifying glass"
              />
            </LocaleWrapper>
          </LocaleSubmenuContent>
          <MenuOpenOverlay className="locale-close-menu" isActive={show} />
        </>
      );
    }
  };

  /**
   * If on the Search page, selecting the SearchButton focus the search bar component's input
   * element. Otherwise, opens the 'search' submenu in the navbar.
   *
   * @function SearchButton
   */
  const SearchButton = () => {
    return (
      <NavSearchButtonContent
        onClick={() => {
          window.location.pathname.includes('/search')
            ? document.getElementsByClassName('search-input-element')[0]?.focus()
            : toggleMenu(MENU_SEARCH);
          mobileBodyUnlock();
        }}
        selected={
          currentMenu === MENU_SEARCH || (typeof window !== 'undefined' && window.location.pathname.includes('search/'))
        }
      >
        {!isCollapsed ? (
          <>
            <SvgTopNavbarSearchIcon />
            <span>SEARCH</span>
          </>
        ) : (
          <SearchIcon />
        )}
      </NavSearchButtonContent>
    );
  };

  /**
   * The SearchMenu component is a submenu component where the search bar lives. This component is not
   * rendered by the MenuContainer component due to slight styling differences, but still functions
   * the same as the other menus, making using of the toggleMenu() function to render.
   *
   */
  const SearchMenu = () => {
    const [show, setShow] = useState(false);

    // When rendered, clear the search query state so a previous query is not displayed
    const searchActions = useSearchActions();
    useEffect(() => {
      searchActions.setQuery('');
    }, []);

    useEffect(() => {
      if (currentMenu === MENU_SEARCH) {
        setShow(true);
      } else {
        setShow(false);
      }
    }, [currentMenu]);

    const handleSearch = ({ query }) => {
      if (query) {
        const urlParams = new URLSearchParams();
        urlParams.set('query', query);
        if (typeof window !== 'undefined') {
          window.location.href = `${checkUrlLocale()}/search/?` + urlParams.toString();
        }
      }
    };

    return (
      <>
        <NavSearchBarWrapper className="search" show={show} mobileVisible={show}>
          <NavSearchBarContainer>
            <CustomSearchBar handleSearch={handleSearch} />
          </NavSearchBarContainer>
        </NavSearchBarWrapper>
        {/* When search bar is open, don't want to close on hover over overlay, must click overlay */}
        <MenuOpenOverlay className="search-close-menu" isActive={show} />
      </>
    );
  };

  return (
    <>
      <HeaderWrapper className="header">
        {!isCollapsed && <TopNavbar />}
        <Navbar />
        {isCollapsed && currentMenu === MENU_MOBILE && <MobileMenu />}
      </HeaderWrapper>
    </>
  );
};

const MenuOpenOverlay = styled.div`
  position: fixed;
  width: 100%;
  z-index: -100;
  top: 122px;
  left: 0px;
  height: 100%;
  background: ${colors.midnightBlack};

  visibility: ${({ isActive }) => (isActive ? 'visible' : 'hidden')};
  opacity: ${({ isActive }) => (isActive ? 0.3 : 0)};
  transition: ${animation.general.transition};
`;

const HeaderWrapper = styled.header`
  background-color: ${colors.pureWhite};
  position: sticky;
  top: 0;
  z-index: 998;
  border-bottom: 1px solid ${colors.gray3};
`;

const NavbarWrapper = styled.div`
  @media screen and (width > ${media.largeTop}) {
    background-color: ${colors.pureWhite};
    position: relative;
    z-index: 1;
  }
`;

const TopNavbarWrapper = styled.div`
  @media screen and (min-width: ${media.largeTop}) {
    background-color: ${colors.pureWhite};
    position: relative;
    z-index: 1;
    padding: 4px 0;
    border-bottom: 1px solid ${colors.gray3};

    button,
    a {
      font-size: 13px;
    }
  }
`;

const TopNavbarContainer = styled(Container)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 32px;
  padding-top: 0;
  padding-bottom: 0;
`;

const NavbarContainer = styled(Container)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  padding-top: 0px;
  padding-bottom: 0px;

  @media screen and (max-width: ${media.largeTop}) {
    height: 72px;
  }
`;

const IconDiv = styled.div`
  cursor: pointer;
`;

const NavbarMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  gap: 20px;
`;

const NavbarSection = styled.div`
  height: 100%;
`;

const MenuWrapper = styled.div`
  display: flex;
  @media screen and (max-width: ${media.largeTop}) {
    display: none;
  }

  width: 100%;
  height: 100%;
  justify-content: center;

  ${({ sectionEnabled }) =>
    sectionEnabled &&
    css`
      &:hover {
        button {
          color: ${colors.crederaRed};
        }
        .submenu {
          opacity: 1;
          visibility: visible;
          transition: ${animation.general.transition};
        }
        ~ .close-menu {
          opacity: 0.3;
          visibility: visible;
          transition: ${animation.general.transition};
        }
      }
      .submenu,
      ~ .close-menu {
        opacity: 0;
        visibility: hidden;
        transition: ${animation.general.transition};
      }
    `}
`;

const MenuButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 8px 8px 12px;
  background-color: transparent;
  color: ${colors.deepSmoke};

  &:hover {
    background-color: transparent;
  }
`;

const MenuLink = styled(InternalLink)`
  color: ${colors.labelDefault};
  font-size: 14px;
  letter-spacing: 0.14px;
  padding: 8px 12px;

  &:hover {
    color: ${colors.crederaRed};
  }
`;

const SubmenuWrapper = styled.div`
  position: absolute;
  width: 100%;
  left: 0px;
  top: 80px;
  display: flex;
  flex-direction: column;
  visibility: hidden;
  opacity: 0;

  @media screen and (max-width: ${media.largeTop}) {
    opacity: ${({ mobileShow }) => (mobileShow ? 1 : 0)};
    visibility: ${({ mobileShow }) => (mobileShow ? 'visible' : 'hidden')};
    top: 0px;
    height: 100dvh;
    overflow-y: scroll;
  }
`;

const SubmenuContainer = styled.div`
  background-color: ${colors.gray5};
  width: 100%;
  text-align: left;
  padding: 0;
  margin: 0;
  border-top: 1px solid ${colors.gray3};

  position: absolute;
  left: 0px;
  top: 0px;

  overflow-y: auto;

  // Hide Scrollbars But Keep Functionality
  ::-webkit-scrollbar {
    // Chrome, Safari, and Opera
    display: none;
  }
  -ms-overflow-style: none; // IE and Edge
  scrollbar-width: none; // Firefox

  @media screen and (max-width: ${media.largeTop}) {
    height: 100%;
  }
`;

const SubmenuContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  @media screen and (max-width: ${media.largeTop}) {
    align-items: stretch;
  }
`;

const SubmenuContent = styled(Container)`
  position: relative;
  display: flex;
  gap: ${props => (props.hasOfferings ? '80px' : '100px')};
  justify-content: ${({ justifyLeft }) => (justifyLeft ? 'flex-start' : 'space-between')};
  width: 100%;

  @media screen and (width > ${media.largeTop}) {
    padding-top: 40px;
  }
  @media screen and (max-width: ${media.largeTop}) {
    padding-top: 20px;
    flex-direction: column;
    gap: 0;
    margin: 0;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    display: ${({ mobileVisible }) => (mobileVisible ? 'flex' : 'none')};
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const SectionOverview = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${props => (props.withHeroImage ? '320px' : '400px')};

  @media screen and (max-width: ${media.largeTop}) {
    max-width: initial;
    flex-direction: row;
    justify-content: space-between;
    gap: 100px;
  }
`;

const Overview = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 48px;

  @media screen and (max-width: ${media.largeTop}) {
    max-width: 500px;
    margin: 0;
    flex: 1;
  }
`;

const SectionTitle = styled(Heading)`
  font-weight: 500;
`;

const SectionDescription = styled(Text)`
  line-height: 24px;
  margin-top: 4px;
  margin-bottom: 16px;

  @media screen and (max-width: ${media.largeTop}) {
    margin-top: 8px;
    margin-bottom: 12px;
  }
`;

const OverviewLink = styled(InternalLink)`
  &:hover {
    text-decoration: underline;
  }
`;

const OverviewExternalLink = styled(ExternalLink)`
  &:hover {
    text-decoration: underline;
  }
`;

const SectionOfferingsList = styled.ul`
  display: block;
  columns: ${({ hasLinksWithoutOfferings }) => (hasLinksWithoutOfferings ? '2' : '3')};
  flex: 1;
  padding-top: 20px;
  margin: 0;

  ul,
  li {
    list-style: none;
    margin: 0;
  }

  @media screen and (max-width: ${media.largeTop}) {
    padding: 0;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    columns: 2;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    columns: 1;
  }
`;

const SectionOfferingsLI = styled.li`
  break-inside: avoid;
  padding-bottom: 32px;

  li {
    margin-bottom: 4px;
  }

  a {
    font-family: Lato, sans-serif;
  }

  @media screen and (max-width: ${media.largeTop}) {
    padding-bottom: 24px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    padding-bottom: 8px;
  }
`;

const SectionLinksColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  
  @media screen and (max-width: ${media.largeTop}) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const SectionLinksContainer = styled.div`
  width: 220px;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  a {
    font-family: Lato, sans-serif;
  }

  @media screen and (max-width: ${media.largeTop}) {
    padding: 0;
    width: auto;
    gap: 16px;
  }
`;

const SectionLink = styled(InternalLink)`
  color: ${colors.midnightBlack};
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
  text-transform: inherit;
  margin-bottom: 4px;

  @media screen and (max-width: ${media.mediumBottom}) {
    font-size: 16px;
  }
`;

const OfferingLink = styled(InternalLink)`
  color: ${colors.midnightBlack};
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-transform: inherit;
  padding-left: 28px;
`;

const SectionLeftHero = styled(GatsbyImage)`
  padding: 0;
  width: 320px;
  height: 213px;
  object-fit: cover;
`;

const SectionRightHero = styled(GatsbyImage)`
  margin: 0;
  height: 380px;
  width: 380px;

  @media screen and (max-width: ${media.largeTop}) {
    width: 248px;
    height: 196px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    display: none;
  }
`;

const FeaturedContentBanner = styled.div`
  position: relative;
  min-width: 100%;
  z-index: 1;
  background: ${colors.oceanBlue};

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(270deg, ${colors.linkHover2} 0%, ${colors.linkHover} 51.11%);
    z-index: -1;
    transition: opacity ${animation.general.durationAndTiming};
    opacity: 0;
  }

  &:hover:before {
    opacity: 1;
  }
`;

const FeaturedContentContainer = styled(Container)`
  display: flex;
  justify-content: space-between;
  padding-top: 20px;
  padding-bottom: 20px;
  align-items: center;
`;

const FeaturedContentBannerTagline = styled(Text)`
  color: ${colors.pureWhite};
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
`;

const FeaturedContentBannerLink = styled(InternalLink)`
  color: ${colors.pureWhite};

  &:hover {
    color: ${colors.pureWhite};
    text-decoration: underline;
  }
`;

const MenuTrigger = styled.img`
  display: flex;
  cursor: pointer;
  margin: 0;
`;

const MobileMenuWrapper = styled.div`
  position: absolute;
  background-color: ${colors.pureWhite};
  top: 0px;
  height: 100vh;
  width: 100%;
  left: 0px;
  overflow-y: scroll;
`;

const MobileMenuContent = styled(Container)`
  display: none;

  ${MenuLink} {
    font-size: 16px;
    letter-spacing: 0.16px;
    padding: 0;
  }

  @media screen and (max-width: ${media.largeTop}) {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 20px;

    height: calc(100% - 72px); //72px for the close button
  }

  @media screen and (max-width: ${media.mediumTop}) {
    padding: 20px;
  }
`;

const PrimaryMobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  width: 100%;
`;

const SecondaryMobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
`;

const MobileMenuButton = styled(Button)`
  color: ${colors.midnightBlack};
  font-family: Tiempos Headline;

  padding: 0;
  text-transform: none;
  background-color: transparent;

  font-weight: 500;
  font-size: ${fontSizes.h1.fontSize};
  line-height: ${fontSizes.h1.lineHeight};

  @media screen and (max-width: ${media.mediumBottom}) {
    font-size: ${fontSizes.h1.mobile.fontSize};
    line-height: ${fontSizes.h1.mobile.lineHeight};
  }
  &:hover {
    background-color: transparent;
  }
`;

const MobileMenuLink = styled(InternalLink)`
  color: ${colors.midnightBlack};
  font-family: Tiempos Headline;
  font-size: 26px;
  font-weight: 500;
  line-height: 36px;
  text-transform: none;
`;

const MobileSpacer = styled.hr`
  margin: 24px 0 24px 0;
  height: 1px;
  background-color: ${colors.gray3};
`;

const MobileLocaleButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.labelDefault};
  padding: 0;
  background-color: transparent;
  font-size: 16px;
  letter-spacing: 0.16px;

  svg {
    transition: ${animation.general.transition};
  }

  &:hover {
    background-color: transparent;
    color: ${colors.crederaRed};

    svg {
      fill: ${colors.crederaRed};
    }
  }
`;

const MobileFeaturedContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileFeaturedContentTagline = styled(Text)`
  line-height: 26px;
  margin-bottom: 12px;
`;

const LocalePillButton = styled(Button)`
  display: flex;
  align-items: center;
  height: 24px;
  gap: 2px;
  text-align: center;
  padding: 4px 12px;
  justify-content: center;

  color: ${({ selected }) => (selected ? `${colors.pureWhite}` : `${colors.labelDefault}`)};
  background-color: ${({ selected }) => (selected ? `${colors.crederaRed}` : `transparent`)};
  border-color: ${({ selected }) => (selected ? `${colors.crederaRed}` : `${colors.gray3}`)};
  fill: ${({ selected }) => (selected ? `${colors.pureWhite}` : `${colors.labelDefault}`)};

  svg {
    transition: ${animation.general.transition};
    fill: ${({ selected }) => (selected ? `${colors.pureWhite}` : `${colors.labelDefault}`)};
  }

  &:hover svg {
    fill: ${colors.pureWhite};
  }
`;

const LocaleSubmenuContent = styled(SubmenuContent)`
  gap: 20px;
  position: absolute;
  background-color: ${colors.pureWhite};
  box-shadow: 0px 0px 80px 0px rgba(0, 0, 0, 0.06);

  max-width: 100%;
  width: 100%;
  top: 122px;
  left: 0;
  z-index: 100;

  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: ${animation.general.transition};

  @media screen and (width > ${media.hugeBottom}) {
    padding-left: calc(((100vw - 1440px) / 2) + 80px);
    padding-right: calc(((100vw - 1440px) / 2) + 80px);
  }

  @media screen and (max-width: ${media.largeTop}) {
    flex-direction: column;
    justify-content: flex-start;
    top: 0px;
    overflow: scroll;
    height: 100vh;
    gap: 0;
  }
`;

const LocaleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media screen and (max-width: ${media.largeTop}) {
    flex-direction: column;
    height: 100vh;
  }
`;

const LocaleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  min-width: 250px;

  @media screen and (max-width: ${media.mediumTop}) {
    gap: 32px;
    padding-top: 12px;
  }
`;

const LocaleTitle = styled(Heading)`
  font-weight: 500;

  @media screen and (max-width: ${media.largeTop}) {
    font-size: ${fontSizes.h1.fontSize};
    line-height: ${fontSizes.h1.lineHeight};
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    font-size: ${fontSizes.h1.mobile.fontSize} !important;
    line-height: ${fontSizes.h1.mobile.lineHeight} !important;
  }
`;

const LocaleDescription = styled(SectionDescription)`
  margin-top: 4px;
  margin-bottom: 0px;

  @media screen and (max-width: ${media.mediumBottom}) {
    margin-top: 8px;
  }
`;

const MobileDivider = styled.div`
  display: none;

  @media screen and (max-width: ${media.largeTop}) {
    display: flex;
    border-top: 1px solid ${colors.gray3};
  }
`;

const LocaleList = styled.ul`
  margin: 0px;
`;

const LocaleLocationButton = styled(Button)`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
  padding: 0;
  text-transform: none;
  background-color: transparent;

  &:hover {
    background-color: transparent;
    p {
      color: ${colors.linkDefault};
    }
  }
`;

const LocaleText = styled(Heading)`
  font-weight: 500;
  font-size: ${fontSizes.h5.fontSize};
  line-height: ${fontSizes.h5.lineHeight};
`;

const CountryFlag = styled.img`
  margin: 0;
  width: 32px;
  height: 32px;
`;

const LocaleMenuHeroImage = styled.img`
  position: relative;
  width: 586.823px;
  height: 380px;
  margin: 24px 0 0 0;
  flex-shrink: 0;

  @media screen and (max-width: ${media.largeTop}) {
    opacity: 50%;
    margin: 0;
    width: 100%;
    height: auto;
    top: 0px;
  }
`;

const SearchIcon = styled(Search)`
  width: 24px;
  height: 24px;
  color: ${colors.labelDefault};
  transition: color ${animation.general.durationAndTiming};
`;

const NavSearchButtonContent = styled(Button)`
  background-color: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    color: ${({ selected }) => (selected ? `${colors.crederaRed}` : `${colors.labelDefault}`)};
    padding-right: 16px;
    pointer-events: none;
  }

  svg path {
    stroke: ${({ selected }) => (selected ? `${colors.crederaRed}` : `${colors.labelDefault}`)};
  }

  ${SearchIcon} {
    color: ${({ selected }) => (selected ? `${colors.crederaRed}` : `${colors.labelDefault}`)};
  }

  &:hover {
    background-color: transparent;

    svg path {
      stroke: ${colors.crederaRed};
    }

    span {
      color: ${colors.crederaRed};
    }
  }
`;

const NavSearchBarWrapper = styled.div`
  background-color: ${colors.pureWhite};
  box-shadow: 0px 0px 80px 0px rgba(0, 0, 0, 0.06);
  width: 100%;
  position: absolute;
  left: 0px;
  top: 122px;
  z-index: 100;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: ${animation.general.transition};

  @media screen and (max-width: ${media.largeTop}) {
    display: ${({ mobileVisible }) => (mobileVisible ? 'flex' : 'none')};
    top: 73px;
  }
`;

const NavSearchBarContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  padding-top: 24px;
  padding-bottom: 24px;

  @media screen and (max-width: ${media.largeTop}) {
    margin: 0;
    width: 100%;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    padding: 20px;
  }

  .search-bar-container {
    // Makes the container expand as the auto-complete results appear
    height: inherit;

    & > div {
      box-shadow: none;
    }
  }
`;

export default AppHeader;
